import { getAccessToken, parseAccessTokenClaims } from '@beatq/shared';
import { MERCHANT_ROOT_NAV, type MerchantNavItem } from './merchantNav.js';

export type MerchantNavSection = MerchantNavItem['icon'];

/** 07-02 / 06-03 merchant role codes used by NAV-6 filters. */
export const MERCHANT_ROLE = {
  owner: 'ROLE-MER-001',
  manager: 'ROLE-MER-002',
  staff: 'ROLE-MER-003',
  cashier: 'ROLE-MER-004',
  kitchen: 'ROLE-MER-005',
  picker: 'ROLE-MER-006',
  finance: 'ROLE-MER-007',
  rider: 'ROLE-MER-008',
} as const;

const BO = MERCHANT_ROLE.owner;
const BM = MERCHANT_ROLE.manager;
const BS = MERCHANT_ROLE.staff;
const CSH = MERCHANT_ROLE.cashier;
const KIT = MERCHANT_ROLE.kitchen;
const PCK = MERCHANT_ROLE.picker;
const FV = MERCHANT_ROLE.finance;

/**
 * NAV-6 section visibility — 07-03 primary navigation table.
 * Floor modes (kitchen / picker / counter) are not rail sections.
 */
const SECTION_VISIBLE_TO: Record<MerchantNavSection, readonly string[]> = {
  orders: [BO, BM, BS, CSH, KIT, PCK, FV, MERCHANT_ROLE.rider],
  catalogue: [BO, BM, BS],
  inventory: [BO, BM, BS, PCK],
  promotions: [BO, BM, FV],
  finance: [BO, FV],
  reviews: [BO, BM, BS],
  reports: [BO, BM, FV],
  team: [BO, BM],
  settings: [BO, BM],
};

/** Merchant shell routes grouped by primary-nav section (07-03 IA). */
const ROUTE_SECTION: Record<string, MerchantNavSection> = {
  '/merchant-dashboard': 'orders',
  '/order-board': 'orders',
  '/incoming-order': 'orders',
  '/reject-order': 'orders',
  '/order-detail-merchant': 'orders',
  '/adjust-promised-time': 'orders',
  '/order-history-merchant': 'orders',
  '/cancel-order-merchant': 'orders',
  '/courier-status': 'orders',
  '/print-ticket': 'orders',
  '/returned-order': 'orders',
  '/scheduled-orders': 'orders',
  '/orders': 'orders',
  '/catalogue-overview': 'catalogue',
  '/category-list': 'catalogue',
  '/category-editor': 'catalogue',
  '/product-list': 'catalogue',
  '/product-editor': 'catalogue',
  '/variant-editor': 'catalogue',
  '/modifier-group-list': 'catalogue',
  '/modifier-group-editor': 'catalogue',
  '/allergen-editor': 'catalogue',
  '/restricted-product-settings': 'catalogue',
  '/bulk-import': 'catalogue',
  '/branch-overrides': 'catalogue',
  '/menu': 'catalogue',
  '/stock-overview': 'inventory',
  '/stock-adjust': 'inventory',
  '/snooze-item': 'inventory',
  '/bulk-availability': 'inventory',
  '/low-stock-alerts': 'inventory',
  '/batch-and-expiry': 'inventory',
  '/stocktake': 'inventory',
  '/promotion-list': 'promotions',
  '/create-promotion': 'promotions',
  '/promotion-cost-preview': 'promotions',
  '/coupon-codes': 'promotions',
  '/promotion-performance': 'promotions',
  '/promotion-detail': 'promotions',
  '/finance-overview': 'finance',
  '/order-earnings': 'finance',
  '/statements': 'finance',
  '/statement-detail': 'finance',
  '/payouts': 'finance',
  '/finance/beat-pay': 'finance',
  '/finance/chargebacks': 'finance',
  '/finance/reports': 'finance',
  '/finance/transactions': 'finance',
  '/instant-payout': 'finance',
  '/deductions-and-disputes': 'finance',
  '/finance-invoices': 'finance',
  '/trading-dashboard': 'reports',
  '/sales-report': 'reports',
  '/operations-report': 'reports',
  '/product-performance': 'reports',
  '/export-centre': 'reports',
  '/analytics': 'reports',
  '/review-list': 'reviews',
  '/respond-to-review': 'reviews',
  '/report-review': 'reviews',
  '/staff-list': 'team',
  '/invite-staff': 'team',
  '/staff-detail': 'team',
  '/business-settings': 'settings',
  '/notification-settings': 'settings',
  '/integrations-overview': 'settings',
  '/api-clients': 'settings',
  '/webhooks': 'settings',
  '/webhook-deliveries': 'settings',
  '/business-profile': 'settings',
  '/branch-list': 'settings',
  '/create-branch': 'settings',
  '/branch-profile': 'settings',
  '/opening-hours': 'settings',
  '/special-closures': 'settings',
  '/service-area': 'settings',
  '/fulfilment-settings': 'settings',
  '/capacity-settings': 'settings',
  '/branch-status': 'settings',
  '/payout-setup': 'finance',
  '/commercial-terms': 'settings',
  '/delivery-bands': 'settings',
  '/vat-registration': 'settings',
  '/substitution-policy': 'settings',
};

export function getMerchantRoleCodes(): string[] {
  const claims = parseAccessTokenClaims(getAccessToken());
  return Array.isArray(claims.rol) ? claims.rol.filter(Boolean) : [];
}

export function isMerchantNavSectionVisible(
  section: MerchantNavSection,
  roles: readonly string[],
): boolean {
  if (roles.length === 0) return true;
  const allowed = SECTION_VISIBLE_TO[section];
  return roles.some((role) => allowed.includes(role));
}

export function filterMerchantNavForRoles(roles: readonly string[]): MerchantNavItem[] {
  return MERCHANT_ROOT_NAV.filter((item) => isMerchantNavSectionVisible(item.icon, roles));
}

export function resolveMerchantNavSection(pathname: string): MerchantNavSection | null {
  const path = pathname.split('?')[0]?.replace(/\/$/, '') || '/';
  if (path === '/') return 'orders';
  return ROUTE_SECTION[path] ?? null;
}

export function merchantHomeHref(roles: readonly string[]): string {
  const visible = filterMerchantNavForRoles(roles);
  return visible[0]?.href ?? '/order-board';
}

function roleCanAccess(allowed: readonly string[], roles: readonly string[]): boolean {
  if (roles.length === 0) return true;
  return roles.some((role) => allowed.includes(role));
}

/**
 * 07-02 Reports children (NAV-6 omit, do not disable):
 * 076/077 BO+BM+FV; 078/079 BO+BM (not FV); 080 BO+FV (not BM).
 */
const REPORTS_CHILD_VISIBLE_TO: Record<string, readonly string[]> = {
  '/trading-dashboard': [BO, BM, FV],
  '/sales-report': [BO, BM, FV],
  '/operations-report': [BO, BM],
  '/product-performance': [BO, BM],
  '/export-centre': [BO, FV],
};

/** 07-02 Orders children. Board is the 07-03 landing for every Orders role. */
const ORDERS_CHILD_VISIBLE_TO: Record<string, readonly string[]> = {
  'orders-nav-board': [BO, BM, BS, CSH, KIT, PCK, FV, MERCHANT_ROLE.rider],
  'orders-nav-history': [BO, BM, BS, FV],
  'orders-nav-scheduled': [BO, BM, BS],
  'orders-nav-floor': [BO, BM, BS, CSH, KIT, PCK],
};

/** 07-02 Catalogue children. BS is read on 019/022 only. */
const CATALOGUE_CHILD_VISIBLE_TO: Record<string, readonly string[]> = {
  'menu-nav-overview': [BO, BM, BS],
  'menu-nav-categories': [BO, BM],
  'menu-nav-products': [BO, BM, BS],
  'menu-nav-modifiers': [BO, BM],
  'menu-nav-import': [BO, BM],
  'menu-nav-overrides': [BO, BM],
};

/** 07-02 Inventory children. PCK is on 031/032/033/036, not 034/035. */
const INVENTORY_CHILD_VISIBLE_TO: Record<string, readonly string[]> = {
  'inventory-nav-overview': [BO, BM, BS, PCK],
  'inventory-nav-adjust': [BO, BM, BS, PCK],
  'inventory-nav-stocktake': [BO, BM],
  'inventory-nav-snooze': [BO, BM, BS, PCK],
  'inventory-nav-bulk': [BO, BM, BS],
  'inventory-nav-alerts': [BO, BM],
  'inventory-nav-batch': [BO, BM, BS, PCK],
};

/** 07-02 Finance children. Instant payout 048 and payout setup 007 are BO only. */
const FINANCE_CHILD_VISIBLE_TO: Record<string, readonly string[]> = {
  'finance-nav-overview': [BO, FV],
  'finance-nav-earnings': [BO, FV],
  'finance-nav-transactions': [BO, FV],
  'finance-nav-statements': [BO, FV],
  'finance-nav-reports': [BO, FV],
  'finance-nav-payouts': [BO, FV],
  'finance-nav-beat-pay': [BO],
  'finance-nav-chargebacks': [BO],
  'finance-nav-instant': [BO],
  'finance-nav-deductions': [BO, FV],
  'finance-nav-invoices': [BO, FV],
};

/**
 * 07-03 Settings nest only. 082 landing is BO; BM partial (keeps tax/printing).
 * Integrations 083 is BO only (NAV-6 omit).
 */
const SETTINGS_CHILD_VISIBLE_TO: Record<string, readonly string[]> = {
  'settings-nav-profile': [BO, BM],
  'settings-nav-branches': [BO, BM],
  'settings-nav-hours': [BO, BM],
  'settings-nav-service-area': [BO, BM],
  'settings-nav-fulfilment': [BO, BM],
  'settings-nav-capacity': [BO, BM],
  'settings-nav-notifications': [BO, BM],
  'settings-nav-tax-printing': [BO, BM],
  'settings-nav-integrations': [BO],
  'settings-nav-delivery-bands': [BO, BM],
  'settings-nav-vat': [BO],
};

/** SCR-MER-018 pause/resume — BO, BM, BS. NAV-6: omit the header control otherwise. */
const TRADING_PAUSE_ROLES = [BO, BM, BS] as const;

export function isReportsSubnavHrefVisible(href: string, roles: readonly string[]): boolean {
  const path = href.split('?')[0]?.replace(/\/$/, '') || '/';
  const allowed = REPORTS_CHILD_VISIBLE_TO[path];
  if (!allowed) return false;
  return roleCanAccess(allowed, roles);
}

export function isSubnavTestIdVisible(
  testId: string,
  roles: readonly string[],
  matrix: Record<string, readonly string[]>,
): boolean {
  const allowed = matrix[testId];
  if (!allowed) return true;
  return roleCanAccess(allowed, roles);
}

export function filterSubnavItemsByTestId<T extends { testId: string }>(
  items: readonly T[],
  roles: readonly string[],
  matrix: Record<string, readonly string[]>,
): T[] {
  return items.filter((item) => isSubnavTestIdVisible(item.testId, roles, matrix));
}

export function filterOrdersSubnavItems<T extends { testId: string }>(
  items: readonly T[],
  roles: readonly string[],
): T[] {
  return filterSubnavItemsByTestId(items, roles, ORDERS_CHILD_VISIBLE_TO);
}

export function filterCatalogueSubnavItems<T extends { testId: string }>(
  items: readonly T[],
  roles: readonly string[],
): T[] {
  return filterSubnavItemsByTestId(items, roles, CATALOGUE_CHILD_VISIBLE_TO);
}

export function filterInventorySubnavItems<T extends { testId: string }>(
  items: readonly T[],
  roles: readonly string[],
): T[] {
  return filterSubnavItemsByTestId(items, roles, INVENTORY_CHILD_VISIBLE_TO);
}

export function filterFinanceSubnavItems<T extends { testId: string }>(
  items: readonly T[],
  roles: readonly string[],
): T[] {
  return filterSubnavItemsByTestId(items, roles, FINANCE_CHILD_VISIBLE_TO);
}

export function filterSettingsSubnavItems<T extends { testId: string }>(
  items: readonly T[],
  roles: readonly string[],
): T[] {
  return filterSubnavItemsByTestId(items, roles, SETTINGS_CHILD_VISIBLE_TO);
}

/** 07-02 SCR-MER-018 actors. Empty roles: keep visible (same as rail). */
export function shouldShowTradingPauseControl(roles: readonly string[]): boolean {
  return roleCanAccess(TRADING_PAUSE_ROLES, roles);
}

export function filterReportsSubnavItems<T extends { to: string }>(
  items: readonly T[],
  roles: readonly string[],
): T[] {
  return items.filter((item) => isReportsSubnavHrefVisible(item.to, roles));
}

/** Expected Reports subnav hrefs per QA role that can open Reports. */
export const EXPECTED_REPORTS_SUBNAV_BY_QA_ROLE: Record<
  string,
  { required: string[]; forbidden: string[] }
> = {
  owner: {
    required: [
      '/trading-dashboard',
      '/sales-report',
      '/operations-report',
      '/product-performance',
      '/export-centre',
    ],
    forbidden: [],
  },
  manager: {
    required: ['/trading-dashboard', '/sales-report', '/operations-report', '/product-performance'],
    forbidden: ['/export-centre'],
  },
  finance: {
    required: ['/trading-dashboard', '/sales-report', '/export-centre'],
    forbidden: ['/operations-report', '/product-performance'],
  },
};

/** Expected primary-rail hrefs per QA role key (NAV-6 matrix). */
export const EXPECTED_NAV_BY_QA_ROLE: Record<
  string,
  { count: number; required: string[]; forbidden: string[] }
> = {
  owner: {
    count: 9,
    required: MERCHANT_ROOT_NAV.map((item) => item.href),
    forbidden: ['/kitchen-display'],
  },
  manager: {
    count: 8,
    required: [
      '/order-board',
      '/catalogue-overview',
      '/stock-overview',
      '/promotion-list',
      '/trading-dashboard',
      '/review-list',
      '/staff-list',
      '/business-settings',
    ],
    forbidden: ['/finance-overview', '/kitchen-display'],
  },
  kitchen: {
    count: 1,
    required: ['/order-board'],
    forbidden: [
      '/kitchen-display',
      '/finance-overview',
      '/staff-list',
      '/business-settings',
      '/catalogue-overview',
      '/promotion-list',
      '/trading-dashboard',
      '/stock-overview',
      '/review-list',
    ],
  },
  finance: {
    count: 4,
    required: ['/order-board', '/promotion-list', '/finance-overview', '/trading-dashboard'],
    forbidden: [
      '/kitchen-display',
      '/staff-list',
      '/business-settings',
      '/catalogue-overview',
      '/stock-overview',
      '/review-list',
    ],
  },
  cashier: {
    count: 1,
    required: ['/order-board'],
    forbidden: [
      '/kitchen-display',
      '/finance-overview',
      '/staff-list',
      '/business-settings',
      '/catalogue-overview',
      '/promotion-list',
      '/trading-dashboard',
      '/stock-overview',
      '/review-list',
    ],
  },
  picker: {
    count: 2,
    required: ['/order-board', '/stock-overview'],
    forbidden: [
      '/kitchen-display',
      '/finance-overview',
      '/staff-list',
      '/business-settings',
      '/catalogue-overview',
      '/promotion-list',
      '/trading-dashboard',
      '/review-list',
    ],
  },
  staff: {
    count: 4,
    required: ['/order-board', '/catalogue-overview', '/stock-overview', '/review-list'],
    forbidden: [
      '/kitchen-display',
      '/finance-overview',
      '/staff-list',
      '/business-settings',
      '/promotion-list',
      '/trading-dashboard',
    ],
  },
};
